import { useEffect, useMemo, useState } from 'react';

const DEFAULT_DISPLAY_POLICY = Object.freeze({
  evidenceStrictMode: true,
  evidenceHitScoreThreshold: 0.05,
});

function resolveStorageKey({ userId, projectId }) {
  return `flare:display-policy:${String(userId || 'anonymous').trim()}:${String(projectId || 'default').trim()}`;
}

function sanitizeBoolean(value, fallback) {
  if (typeof value !== 'boolean') {
    return fallback;
  }
  return value;
}

function sanitizeThreshold(value) {
  const normalized = Number(value);
  if (!Number.isFinite(normalized)) {
    return DEFAULT_DISPLAY_POLICY.evidenceHitScoreThreshold;
  }
  if (normalized < 0) {
    return 0;
  }
  if (normalized > 1) {
    return 1;
  }
  return normalized;
}

function sanitizeProfile(payload) {
  const source = payload && typeof payload === 'object' ? payload : {};
  return {
    evidenceStrictMode: sanitizeBoolean(
      source.evidenceStrictMode,
      DEFAULT_DISPLAY_POLICY.evidenceStrictMode,
    ),
    evidenceHitScoreThreshold: sanitizeThreshold(source.evidenceHitScoreThreshold),
  };
}

function loadProfileFromStorage(storageKey) {
  if (typeof window === 'undefined' || !window.localStorage) {
    return DEFAULT_DISPLAY_POLICY;
  }
  const raw = window.localStorage.getItem(storageKey);
  if (!raw) {
    return DEFAULT_DISPLAY_POLICY;
  }
  try {
    return sanitizeProfile(JSON.parse(raw));
  } catch {
    return DEFAULT_DISPLAY_POLICY;
  }
}

export function useUserDisplayPolicy({
  userId,
  projectId,
} = {}) {
  const storageKey = useMemo(() => resolveStorageKey({ userId, projectId }), [projectId, userId]);
  const [profile, setProfile] = useState(() => loadProfileFromStorage(storageKey));

  useEffect(() => {
    setProfile(loadProfileFromStorage(storageKey));
  }, [storageKey]);

  useEffect(() => {
    if (typeof window === 'undefined' || !window.localStorage) {
      return;
    }
    window.localStorage.setItem(storageKey, JSON.stringify(profile));
  }, [profile, storageKey]);

  return {
    evidenceStrictMode: profile.evidenceStrictMode,
    setEvidenceStrictMode: (value) => setProfile((prev) => ({
      ...prev,
      evidenceStrictMode: Boolean(value),
    })),
    evidenceHitScoreThreshold: profile.evidenceHitScoreThreshold,
    setEvidenceHitScoreThreshold: (value) => setProfile((prev) => ({
      ...prev,
      evidenceHitScoreThreshold: sanitizeThreshold(value),
    })),
  };
}

export default useUserDisplayPolicy;
